import {
    NavigationBar as NavigationBarShared,
    SiteNavItemPropsList,
    SiteNavProps,
    useAnalyticsActionTracker,
    useOidcAuthentication,
} from '@cp-shared-19/frontend-ui';
import { NotificationsCenter } from 'components/notification-center/NotificationsCenter';
import { IncidentNotification } from 'components/incident-notification';
import { TFunction } from 'i18next';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { currentBrand, payByPhoneEnabled } from '../../config';
import {
    changeContactDetailsPath,
    contactDetailsCheckPagePath,
    cookiePolicyPath,
    dashboardPagePath,
    faqPagePath,
    forbiddenPagePath,
    landingPagePath,
    legalNoticePath,
    logoutPagePath,
    myDocumentsPath,
    myProfilePagePath,
    myRequestsPagePath,
    notAuthorizedPagePath,
    notFoundPagePath,
    parkingPagePath,
    privacyPolicyPath,
    registrationPagePath,
    requestHistoryPagePath,
} from './paths';

const pagesWithoutNavbar = [contactDetailsCheckPagePath()];

const pagesWithoutStaticHeader = [
    landingPagePath(),
    logoutPagePath(),
    faqPagePath(),
    cookiePolicyPath(),
    legalNoticePath(),
    privacyPolicyPath(),
    notAuthorizedPagePath(),
    notFoundPagePath(),
    forbiddenPagePath(),
    contactDetailsCheckPagePath(),
];

function privateKYCNavigationLinks(t: TFunction, logout: () => Promise<void>): SiteNavItemPropsList {
    return [
        {
            label: t('navigation.logout'),
            onClick: logout,
            href: '#',
        },
    ];
}

function publicNavigationLinks(t: TFunction, login: () => Promise<void>): SiteNavItemPropsList {
    return [
        {
            label: t('navigation.login'),
            onClick: login,
            href: '#',
        },
    ];
}

function privateNavigationLinks(t: TFunction, logout: () => Promise<void>): SiteNavItemPropsList {
    return [
        {
            url: dashboardPagePath(),
            label: t('navigation.dashboard'),
            href: dashboardPagePath(),
        },
        ...(payByPhoneEnabled
            ? [
                  {
                      url: parkingPagePath(),
                      label: t('navigation.parking'),
                      href: parkingPagePath(),
                  },
              ]
            : []),
        {
            url: myDocumentsPath(),
            href: myDocumentsPath(),
            label: t('navigation.documents'),
        },
        {
            url: myProfilePagePath(),
            href: myProfilePagePath(),
            label: t('navigation.profile'),
        },
        {
            url: myRequestsPagePath(),
            label: t('navigation.requests'),
            href: myRequestsPagePath(),
        },
        {
            url: requestHistoryPagePath(),
            href: requestHistoryPagePath(),
            label: t('navigation.requestHistory'),
        },
        {
            url: faqPagePath(),
            href: faqPagePath(),
            label: t('navigation.faq'),
        },
        {
            label: t('navigation.logout'),
            href: '#',
            onClick: logout,
        },
    ];
}

export const NavigationBar: React.FC = () => {
    const { t, i18n } = useTranslation('navigation');
    const { isAuthenticated, logout, login } = useOidcAuthentication();
    const currentPathName = useLocation().pathname;
    const language = i18n.languages[0];
    const history = useHistory();
    const location = useLocation();
    const currentPageName = location.pathname.split('/')[1];
    const { onAction: onLogin } = useAnalyticsActionTracker('login');
    const { onAction: onLogout } = useAnalyticsActionTracker('logout');

    const isRegistrationPage = location.pathname === registrationPagePath();
    const isDashboardPage = location.pathname === dashboardPagePath();
    const isChangeContactPage = location.pathname === changeContactDetailsPath();
    const logoDisabled = !isAuthenticated || isRegistrationPage || isDashboardPage || isChangeContactPage;

    useEffect(() => {
        const applyAria = () => {
            document.querySelectorAll('svg').forEach((svg) => {
                svg.setAttribute('aria-hidden', 'true');
                svg.setAttribute('focusable', 'false');
            });
        };

        applyAria();

        const observer = new MutationObserver(applyAria);

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        return () => observer.disconnect();
    }, []);

    /**
     * Workaround for a bronson-react SiteNav bug affecting the VW6 theme only: plain nav links
     * (`.c-site-nav__link` anchors without a dropdown) close the whole off-canvas menu on `mousedown`
     * (before the `click` fires). For VW6 the menu panel is an always-on off-canvas overlay, so closing
     * it moves/hides the link out from under the cursor before `mouseup`, which makes the browser drop
     * the `click` event entirely and the navigation never happens. Stopping the `mousedown` from
     * reaching bronson's handler (capture phase, before React's bubble-phase listener) prevents the
     * premature close so the native `click` can fire and trigger navigation as expected.
     */
    useEffect(() => {
        const handleMouseDown = (event: MouseEvent): void => {
            const target = event.target as HTMLElement | null;
            const link = target?.closest('.c-site-nav__link');
            if (link?.tagName === 'A') {
                event.stopPropagation();
            }
        };

        document.addEventListener('mousedown', handleMouseDown, { capture: true });

        return () => document.removeEventListener('mousedown', handleMouseDown, { capture: true });
    }, []);

    // Close the off-canvas menu ourselves after navigating, since the mousedown-based auto-close above is disabled.
    useEffect(() => {
        const toggle = document.querySelector<HTMLElement>('.c-site-nav__toggle[aria-expanded="true"]');
        toggle?.click();
    }, [location.pathname]);

    const getNavigationConfig = () => {
        const getLogoutHandler = () => {
            onLogout(currentPageName);
            return logout({
                post_logout_redirect_uri: `${window.location.origin}${logoutPagePath(true)}`,
            });
        };

        const getLoginHandler = () => {
            onLogin(currentPageName);
            return login({
                redirect_uri: `${window.location.origin}${dashboardPagePath()}`,
                redirectTarget: 'self',
                redirectMethod: 'assign',
                ui_locales: language,
            });
        };

        if (!isAuthenticated) {
            return publicNavigationLinks(t, getLoginHandler);
        }

        return isRegistrationPage
            ? privateKYCNavigationLinks(t, getLogoutHandler)
            : privateNavigationLinks(t, getLogoutHandler);
    };

    const navigationItems = getNavigationConfig().map((navItem) => ({
        isActive: history.location.pathname === navItem.url,
        ...navItem,
    }));

    const getLogoAriaLabel = () => {
        if (currentBrand.toUpperCase() == 'VW') {
            return 'Logo Volkswagen';
        }
        if (currentBrand.toUpperCase() == 'VWFS') {
            return 'Logo Volkswagen Financial Services';
        }
        if (currentBrand.toUpperCase() == 'VWCV') {
            return 'Logo Volkswagen Véhicules Utilitaires';
        }

        return `Logo ${currentBrand.charAt(0).toUpperCase()}${currentBrand.slice(1)}`;
    };

    const hidden = pagesWithoutNavbar.includes(currentPathName);
    const withStaticHeader = !pagesWithoutStaticHeader.includes(currentPathName);

    const incidentNotificationPage =
        currentPathName === landingPagePath()
            ? 'landing'
            : currentPathName === dashboardPagePath()
            ? 'dashboard'
            : undefined;

    const navigation: SiteNavProps = {
        navigationItems,
    };

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    const handleClickLogoRedirect = () => {
        const logoUrl = isAuthenticated && !isRegistrationPage ? dashboardPagePath() : '/';
        history.push(logoUrl);
    };

    return (
        <>
            {incidentNotificationPage && <IncidentNotification page={incidentNotificationPage} />}
            <NavigationBarShared
                logoDisableLink={logoDisabled}
                logoSource={`/${currentBrand}/img/logo.svg`}
                logoAltText={logoDisabled ? t(`navigation.logo-title.${currentBrand}`) : t('navigation.aria-title')}
                headerRole="banner"
                navigation={navigation}
                withStaticHeader={withStaticHeader}
                hidden={hidden}
                notificationsCenter={isAuthenticated && !isRegistrationPage ? <NotificationsCenter /> : <></>}
                href={isAuthenticated && !isRegistrationPage ? dashboardPagePath() : undefined}
                alt={getLogoAriaLabel()}
                onLogoClick={handleClickLogoRedirect}
            />
        </>
    );
};
